import React, { useState, useEffect } from 'react';
import PlayerSilhouette from './PlayerSilhouette';
import { getSampleMinorLeagueStats } from '../services/playersFixtures';
import fantasyDataService from '../services/fantasyDataService';
import graphQLApiService from '../services/apiGraphQL';
import draftService from '../services/draftService';

function PlayerProfile({ player, isOpen, onClose, leagueId }) {
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // General tab data
  const [latestNews, setLatestNews] = useState(null);
  const [seasonStats2025, setSeasonStats2025] = useState(null);
  const [last15Games, setLast15Games] = useState([]);
  
  // Stats tab data
  const [mlbStats, setMlbStats] = useState([]);

  // Splits tab data
  const [splitsData, setSplitsData] = useState(null);
  const [splitsPeriod, setSplitsPeriod] = useState('current'); // 'current', 'previous', 'career'
  
  // News tab data
  const [newsItems, setNewsItems] = useState([]);
  const [newsPage, setNewsPage] = useState(1);
  const [loadingMoreNews, setLoadingMoreNews] = useState(false);
  
  // Transactions tab data
  const [transactions, setTransactions] = useState([]);

  // Get player ID (handle both string and numeric IDs)
  const getPlayerId = () => {
    if (!player) return null;
    return player.fantasyDataPlayerId || player.id || player.playerId;
  };

  // Load general tab data
  useEffect(() => {
    if (!isOpen || !player || activeTab !== 'general') return;
    
    const loadGeneralData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const playerId = getPlayerId();
        if (!playerId) throw new Error('Player ID not found');
        
        // Load latest news
        try {
          const news = await fantasyDataService.getNewsByPlayer(playerId);
          if (news && news.length > 0) {
            setLatestNews(news[0]);
          }
        } catch (err) {
          console.warn('Error loading latest news:', err);
        }
        
        // Load 2025 season stats
        try {
          const stats = await fantasyDataService.getPlayerSeasonStats('2025');
          const playerStats = stats.find(s => s.PlayerID === parseInt(playerId));
          if (playerStats) {
            setSeasonStats2025(playerStats);
          }
        } catch (err) {
          console.warn('Error loading 2025 stats:', err);
        }
        
        // Load last 15 games
        try {
          const today = new Date();
          const games = [];
          let daysChecked = 0;
          let maxDays = 60; // Check up to 60 days back
          
          while (games.length < 15 && daysChecked < maxDays) {
            const date = new Date(today);
            date.setDate(date.getDate() - daysChecked);
            
            const year = date.getFullYear();
            const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            
            try {
              const dayStats = await fantasyDataService.getPlayerGameStatsByDate(formattedDate);
              const playerGameStats = dayStats.filter(s => s.PlayerID === parseInt(playerId));
              games.push(...playerGameStats.map(stat => ({
                ...stat,
                date: formattedDate
              })));
            } catch (err) {
              // No stats for this date, continue
            }
            
            daysChecked++;
          }
          
          setLast15Games(games.slice(0, 15));
        } catch (err) {
          console.warn('Error loading last 15 games:', err);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading general data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadGeneralData();
  }, [isOpen, player, activeTab]);

  // Load stats tab data
  useEffect(() => {
    if (!isOpen || !player || activeTab !== 'stats') return;
    
    const loadStatsData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const playerId = getPlayerId();
        if (!playerId) throw new Error('Player ID not found');
        
        // Load MLB stats for multiple seasons
        const seasons = ['2025', '2024', '2023', '2022', '2021'];
        const allStats = [];
        
        for (const season of seasons) {
          try {
            const stats = await fantasyDataService.getPlayerSeasonStats(season);
            const playerStats = stats.find(s => s.PlayerID === parseInt(playerId));
            if (playerStats) {
              allStats.push({ season, ...playerStats });
            }
          } catch (err) {
            // Season might not exist yet, continue
          }
        }
        
        setMlbStats(allStats);
      } catch (err) {
        setError(err.message);
        console.error('Error loading stats data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadStatsData();
  }, [isOpen, player, activeTab]);

  // Load splits tab data
  useEffect(() => {
    if (!isOpen || !player || activeTab !== 'splits') return;
    
    const loadSplitsData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const playerId = getPlayerId();
        if (!playerId) throw new Error('Player ID not found');
        
        // FantasyData API might have splits endpoints
        // For now, we'll use season stats and calculate splits
        // This is a placeholder - actual splits would need specific API endpoints
        const currentSeason = '2025';
        const previousSeason = '2024';
        
        try {
          const currentStats = await fantasyDataService.getPlayerSeasonStats(currentSeason);
          const playerCurrentStats = currentStats.find(s => s.PlayerID === parseInt(playerId));
          
          const previousStats = await fantasyDataService.getPlayerSeasonStats(previousSeason);
          const playerPreviousStats = previousStats.find(s => s.PlayerID === parseInt(playerId));
          
          // Aggregate career stats from all available seasons
          const allSeasons = ['2025', '2024', '2023', '2022', '2021'];
          const careerStats = {
            AtBats: 0,
            Hits: 0,
            HomeRuns: 0,
            RunsBattedIn: 0,
            Runs: 0,
            StolenBases: 0,
            Walks: 0,
            Strikeouts: 0,
            InningsPitchedDecimal: 0,
            EarnedRunsAllowed: 0,
            HitsAllowed: 0,
            WalksAllowed: 0,
            PitcherStrikeouts: 0,
            Wins: 0,
            Losses: 0,
            Saves: 0,
            Games: 0
          };
          
          for (const season of allSeasons) {
            try {
              const stats = await fantasyDataService.getPlayerSeasonStats(season);
              const playerStats = stats.find(s => s.PlayerID === parseInt(playerId));
              if (playerStats) {
                careerStats.AtBats += playerStats.AtBats || 0;
                careerStats.Hits += playerStats.Hits || 0;
                careerStats.HomeRuns += playerStats.HomeRuns || 0;
                careerStats.RunsBattedIn += playerStats.RunsBattedIn || 0;
                careerStats.Runs += playerStats.Runs || 0;
                careerStats.StolenBases += playerStats.StolenBases || 0;
                careerStats.Walks += playerStats.Walks || 0;
                careerStats.Strikeouts += playerStats.Strikeouts || 0;
                careerStats.InningsPitchedDecimal += playerStats.InningsPitchedDecimal || 0;
                careerStats.EarnedRunsAllowed += playerStats.EarnedRunsAllowed || 0;
                careerStats.HitsAllowed += playerStats.HitsAllowed || 0;
                careerStats.WalksAllowed += playerStats.WalksAllowed || 0;
                careerStats.PitcherStrikeouts += playerStats.PitcherStrikeouts || 0;
                careerStats.Wins += playerStats.Wins || 0;
                careerStats.Losses += playerStats.Losses || 0;
                careerStats.Saves += playerStats.Saves || 0;
                careerStats.Games += playerStats.Games || 0;
              }
            } catch (err) {
              // Continue
            }
          }
          
          setSplitsData({
            current: playerCurrentStats,
            previous: playerPreviousStats,
            career: careerStats
          });
        } catch (err) {
          console.warn('Error loading splits:', err);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading splits data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadSplitsData();
  }, [isOpen, player, activeTab, splitsPeriod]);

  // Load news tab data
  useEffect(() => {
    if (!isOpen || !player || activeTab !== 'news') return;
    
    const loadNewsData = async () => {
      if (newsPage === 1) {
        setLoading(true);
      } else {
        setLoadingMoreNews(true);
      }
      setError(null);
      
      try {
        const playerId = getPlayerId();
        if (!playerId) throw new Error('Player ID not found');
        
        const news = await fantasyDataService.getNewsByPlayer(playerId);
        const itemsPerPage = 10;
        const startIndex = (newsPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        
        if (newsPage === 1) {
          setNewsItems(news.slice(0, itemsPerPage));
        } else {
          setNewsItems(prev => [...prev, ...news.slice(startIndex, endIndex)]);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading news data:', err);
      } finally {
        setLoading(false);
        setLoadingMoreNews(false);
      }
    };
    
    loadNewsData();
  }, [isOpen, player, activeTab, newsPage]);

  // Load transactions tab data
  useEffect(() => {
    if (!isOpen || !player || activeTab !== 'transactions' || !leagueId) return;
    
    const loadTransactionsData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const playerId = getPlayerId();
        if (!playerId) throw new Error('Player ID not found');
        
        const allTransactions = [];
        
        // Get draft data to find draft picks
        try {
          const draft = await draftService.getDraft(leagueId);
          if (draft && draft.picks && Array.isArray(draft.picks)) {
            draft.picks.forEach(pick => {
              const pickPlayerId = pick.playerId || pick.PlayerID;
              if (pickPlayerId === playerId || pickPlayerId === playerId.toString() || 
                  pickPlayerId === parseInt(playerId)) {
                allTransactions.push({
                  type: 'Draft',
                  date: pick.timestamp || pick.pickedAt || draft.startedAt,
                  team: pick.teamName || pick.team,
                  description: `Drafted in round ${pick.round || 'N/A'}, pick ${pick.pickInRound || 'N/A'}`
                });
              }
            });
          }
        } catch (draftErr) {
          console.warn('Error loading draft data:', draftErr);
        }
        
        // Check DraftPick events for this player
        try {
          const client = graphQLApiService.getClient?.() || null;
          if (client && client.models && client.models.DraftPick) {
            // Query DraftPick events for this player
            // Note: This would require a custom query or listing all picks
            // For now, we'll rely on draft.picks
          }
        } catch (pickErr) {
          console.warn('Error loading draft picks:', pickErr);
        }
        
        // Sort transactions by date (most recent first)
        allTransactions.sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });
        
        setTransactions(allTransactions);
      } catch (err) {
        setError(err.message);
        console.error('Error loading transactions data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadTransactionsData();
  }, [isOpen, player, activeTab, leagueId]);

  if (!isOpen || !player) return null;

  const playerId = getPlayerId();
  const isPitcher = ['P', 'SP', 'RP', 'RHP', 'LHP'].includes(player.position) || !!player.isPitcher;
  const isProspect = !!(player.prospectRankings || player.prospectEligible);
  const tabs = ['general', ...(isProspect ? ['prospects'] : []), 'stats', 'minors', 'splits', 'news', 'transactions'];
  const minorLeagueRows = getSampleMinorLeagueStats({ id: player.id ?? player.playerId, isPitcher, org: player.team || player.org || 'FA' });

  // Format stat value
  const formatStat = (value, decimals = 3) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') {
      return value.toFixed(decimals);
    }
    return value;
  };

  // Calculate batting average
  const calculateAVG = (hits, atBats) => {
    if (!atBats || atBats === 0) return '0.000';
    return (hits / atBats).toFixed(3);
  };

  // Calculate ERA
  const calculateERA = (earnedRuns, innings) => {
    if (!innings || innings === 0) return '0.00';
    return ((earnedRuns * 9) / innings).toFixed(2);
  };

  // Calculate WHIP
  const calculateWHIP = (hits, walks, innings) => {
    if (!innings || innings === 0) return '0.00';
    return ((hits + walks) / innings).toFixed(2);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {player.photoUrl ? (
              <img
                src={player.photoUrl}
                alt={player.name}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: '#eceef1',
                  color: '#9aa2ab',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <PlayerSilhouette />
              </div>
            )}
            <div>
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                {player.name}
              </h2>
              <div style={{ display: 'flex', gap: '10px', marginTop: '5px', fontSize: '14px', color: '#666' }}>
                <span>{player.position}</span>
                <span>•</span>
                <span>{player.team}</span>
                {player.number && (
                  <>
                    <span>•</span>
                    <span>#{player.number}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px 10px'
            }}
          >
            ×
          </button>
        </div>

        {/* Prospect banner */}
        {isProspect && (
          <div style={{
            padding: '10px 20px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--primary-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: '700', color: 'var(--primary)', fontSize: '13px', letterSpacing: '-0.022em' }}>
              PROSPECT
            </span>
            {player.prospectSources?.slice(0, 4).map((s) => (
              <span
                key={s.source}
                style={{
                  padding: '3px 10px',
                  borderRadius: '9999px',
                  background: 'var(--primary)',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: '700',
                  letterSpacing: '-0.022em',
                }}
              >
                {s.source} #{s.rank}
              </span>
            ))}
            {!player.prospectRankings && (
              <span style={{ fontSize: '13px', color: 'var(--primary)', letterSpacing: '-0.022em' }}>
                Prospect eligible — not yet ranked
              </span>
            )}
            {player.prospectSources?.length > 4 && (
              <span style={{ fontSize: '12px', color: 'var(--primary)', fontWeight: '600' }}>
                +{player.prospectSources.length - 4} more
              </span>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa'
        }}>
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setNewsPage(1);
              }}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab ? '600' : '400',
                color: activeTab === tab ? 'var(--primary)' : 'var(--muted)',
                borderBottom: activeTab === tab ? '2px solid var(--primary)' : '2px solid transparent',
                textTransform: 'capitalize',
                letterSpacing: '-0.022em',
                fontFamily: 'var(--font-family)',
                whiteSpace: 'nowrap',
              }}
            >
              {tab === 'prospects' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  Prospects
                  {player.prospectRankings && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '18px',
                      height: '18px',
                      borderRadius: '9999px',
                      background: activeTab === 'prospects' ? 'var(--primary)' : 'var(--primary-soft)',
                      color: activeTab === 'prospects' ? '#fff' : 'var(--primary)',
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '0 4px',
                    }}>
                      {player.prospectSources?.length ?? 1}
                    </span>
                  )}
                </span>
              ) : tab === 'minors' ? 'Minor Leagues' : tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px'
        }}>
          {loading && activeTab !== 'news' && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Loading...
            </div>
          )}

          {error && (
            <div style={{
              padding: '15px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              Error: {error}
            </div>
          )}

          {/* General Tab */}
          {activeTab === 'general' && !loading && (
            <div>
              {/* Latest News */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>
                  Latest News
                </h3>
                {latestNews ? (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    borderLeft: '3px solid #007bff'
                  }}>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                      {latestNews.Updated && new Date(latestNews.Updated).toLocaleDateString()}
                    </div>
                    <div style={{ fontWeight: '600', marginBottom: '5px' }}>
                      {latestNews.Title}
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {latestNews.Content}
                    </div>
                    {latestNews.Url && (
                      <a
                        href={latestNews.Url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          marginTop: '10px',
                          color: '#007bff',
                          fontSize: '14px',
                          textDecoration: 'none'
                        }}
                      >
                        Read more →
                      </a>
                    )}
                  </div>
                ) : (
                  <div style={{ color: '#666', fontStyle: 'italic' }}>No recent news available</div>
                )}
              </div>

              {/* 2025 Stats */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>
                  2025 Season Stats
                </h3>
                {seasonStats2025 ? (
                  <div className="roster-style-table-wrap">
                    <div>
                    <table className="roster-style-table">
                      <thead>
                        <tr>
                          {isPitcher ? (
                            <>
                              <th>W</th>
                              <th>L</th>
                              <th>SV</th>
                              <th>ERA</th>
                              <th>WHIP</th>
                              <th>K</th>
                              <th>IP</th>
                            </>
                          ) : (
                            <>
                              <th>G</th>
                              <th>AB</th>
                              <th>H</th>
                              <th>AVG</th>
                              <th>HR</th>
                              <th>RBI</th>
                              <th>R</th>
                              <th>SB</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {isPitcher ? (
                            <>
                              <td style={{ padding: '8px' }}>{seasonStats2025.Wins || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.Losses || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.Saves || 0}</td>
                              <td style={{ padding: '8px' }}>{formatStat(seasonStats2025.EarnedRunAverage, 2)}</td>
                              <td style={{ padding: '8px' }}>{formatStat(seasonStats2025.WalksHitsPerInningsPitched, 2)}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.PitcherStrikeouts || seasonStats2025.Strikeouts || 0}</td>
                              <td style={{ padding: '8px' }}>{formatStat(seasonStats2025.InningsPitchedDecimal, 1)}</td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '8px' }}>{seasonStats2025.Games || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.AtBats || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.Hits || 0}</td>
                              <td style={{ padding: '8px' }}>{calculateAVG(seasonStats2025.Hits, seasonStats2025.AtBats)}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.HomeRuns || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.RunsBattedIn || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.Runs || 0}</td>
                              <td style={{ padding: '8px' }}>{seasonStats2025.StolenBases || 0}</td>
                            </>
                          )}
                        </tr>
                      </tbody>
                    </table>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#666', fontStyle: 'italic' }}>No 2025 stats available</div>
                )}
              </div>

              {/* Last 15 Games */}
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>
                  Last 15 Games
                </h3>
                {last15Games.length > 0 ? (
                  <div className="roster-style-table-wrap">
                    <div>
                    <table className="roster-style-table" style={{ fontSize: '14px' }}>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Opp</th>
                          {isPitcher ? (
                            <>
                              <th style={{ textAlign: 'center' }}>IP</th>
                              <th style={{ textAlign: 'center' }}>H</th>
                              <th style={{ textAlign: 'center' }}>ER</th>
                              <th style={{ textAlign: 'center' }}>K</th>
                              <th style={{ textAlign: 'center' }}>W</th>
                            </>
                          ) : (
                            <>
                              <th style={{ textAlign: 'center' }}>AB</th>
                              <th style={{ textAlign: 'center' }}>H</th>
                              <th style={{ textAlign: 'center' }}>HR</th>
                              <th style={{ textAlign: 'center' }}>RBI</th>
                              <th style={{ textAlign: 'center' }}>R</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {last15Games.map((game, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '8px' }}>{game.date}</td>
                            <td style={{ padding: '8px' }}>{game.Opponent || '-'}</td>
                            {isPitcher ? (
                              <>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{formatStat(game.InningsPitchedDecimal, 1)}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.HitsAllowed || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.EarnedRunsAllowed || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.PitcherStrikeouts || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.Wins || 0}</td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.AtBats || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.Hits || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.HomeRuns || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.RunsBattedIn || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{game.Runs || 0}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#666', fontStyle: 'italic' }}>No recent games available</div>
                )}
              </div>
            </div>
          )}

          {/* Prospects Tab */}
          {activeTab === 'prospects' && (
            <div>
              {/* Eligibility header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 16px',
                borderRadius: '12px',
                background: 'var(--primary-soft)',
                border: '1px solid rgba(0,113,227,0.15)',
                marginBottom: '24px',
                flexWrap: 'wrap',
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '18px',
                  flexShrink: 0,
                }}>
                  ★
                </div>
                <div>
                  <div style={{ fontWeight: '700', color: 'var(--primary)', fontSize: '15px', letterSpacing: '-0.022em' }}>
                    Prospect Eligible
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--primary)', opacity: 0.8, letterSpacing: '-0.022em' }}>
                    {player.prospectRankings
                      ? `Ranked by ${player.prospectSources?.length ?? 1} scouting source${(player.prospectSources?.length ?? 1) !== 1 ? 's' : ''}`
                      : 'Not yet ranked by tracked scouting sources'}
                  </div>
                </div>
              </div>

              {/* Rankings table */}
              {player.prospectSources?.length > 0 ? (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.022em', marginBottom: '12px', color: 'var(--foreground)' }}>
                    Prospect Rankings
                  </h3>
                  <div className="roster-style-table-wrap card" style={{ padding: 0 }}>
                    <div>
                      <table className="roster-style-table">
                        <thead>
                          <tr>
                            <th style={{ width: '48px', textAlign: 'center' }}>#</th>
                            <th>Scouting Source</th>
                            <th style={{ textAlign: 'center' }}>Rank</th>
                          </tr>
                        </thead>
                        <tbody>
                          {player.prospectSources.map((s, idx) => (
                            <tr key={s.source}>
                              <td style={{ textAlign: 'center', color: 'var(--muted)', fontSize: '13px' }}>{idx + 1}</td>
                              <td style={{ fontWeight: '600', letterSpacing: '-0.022em' }}>{s.source}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '3px 10px',
                                  borderRadius: '9999px',
                                  background: idx === 0 ? 'var(--primary)' : 'var(--primary-soft)',
                                  color: idx === 0 ? '#fff' : 'var(--primary)',
                                  fontSize: '13px',
                                  fontWeight: '700',
                                  letterSpacing: '-0.022em',
                                }}>
                                  #{s.rank}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.022em', marginBottom: '12px' }}>
                    Prospect Rankings
                  </h3>
                  <div style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: 'var(--muted)',
                    background: 'var(--surface-muted)',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.4 }}>★</div>
                    <p style={{ fontSize: '15px', letterSpacing: '-0.022em' }}>
                      Not yet ranked by tracked scouting services
                    </p>
                  </div>
                </div>
              )}

              {/* Player bio relevant to prospects */}
              <div>
                <h3 style={{ fontSize: '17px', fontWeight: '600', letterSpacing: '-0.022em', marginBottom: '12px', color: 'var(--foreground)' }}>
                  Eligibility Details
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                  {[
                    { label: 'Age', value: player.age ?? '—' },
                    { label: 'Status', value: player.status ?? '—' },
                    { label: 'Position', value: player.position ?? '—' },
                    { label: 'Team', value: player.team ?? '—' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{
                      padding: '12px 14px',
                      background: 'var(--surface-muted)',
                      borderRadius: '12px',
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                        {label}
                      </div>
                      <div style={{ fontSize: '17px', fontWeight: '600', color: 'var(--foreground)', letterSpacing: '-0.022em' }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--muted)', letterSpacing: '-0.022em', lineHeight: '1.5' }}>
                  Prospect eligibility applies to players with ≤ 2 years of MLB service time, age ≤ 23, or active minor-league status.
                </p>
              </div>
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && !loading && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  Annual MLB Stats
                </h3>
              </div>

              {mlbStats.length > 0 ? (
                <div className="roster-style-table-wrap">
                  <div>
                  <table className="roster-style-table">
                    <thead>
                      <tr>
                        <th>Season</th>
                        {isPitcher ? (
                          <>
                            <th style={{ textAlign: 'center' }}>W</th>
                            <th style={{ textAlign: 'center' }}>L</th>
                            <th style={{ textAlign: 'center' }}>SV</th>
                            <th style={{ textAlign: 'center' }}>ERA</th>
                            <th style={{ textAlign: 'center' }}>WHIP</th>
                            <th style={{ textAlign: 'center' }}>K</th>
                            <th style={{ textAlign: 'center' }}>IP</th>
                          </>
                        ) : (
                          <>
                            <th style={{ textAlign: 'center' }}>G</th>
                            <th style={{ textAlign: 'center' }}>AB</th>
                            <th style={{ textAlign: 'center' }}>H</th>
                            <th style={{ textAlign: 'center' }}>AVG</th>
                            <th style={{ textAlign: 'center' }}>HR</th>
                            <th style={{ textAlign: 'center' }}>RBI</th>
                            <th style={{ textAlign: 'center' }}>R</th>
                            <th style={{ textAlign: 'center' }}>SB</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {mlbStats.map((stat, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>{stat.season}</td>
                          {isPitcher ? (
                            <>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.Wins || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.Losses || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.Saves || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{formatStat(stat.EarnedRunAverage, 2)}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{formatStat(stat.WalksHitsPerInningsPitched, 2)}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.PitcherStrikeouts || stat.Strikeouts || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{formatStat(stat.InningsPitchedDecimal, 1)}</td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.Games || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.AtBats || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.Hits || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{calculateAVG(stat.Hits, stat.AtBats)}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.HomeRuns || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.RunsBattedIn || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.Runs || 0}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.StolenBases || 0}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#666', fontStyle: 'italic' }}>No stats available</div>
              )}

            </div>
          )}

          {/* Minor Leagues Tab */}
          {activeTab === 'minors' && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 16px' }}>Minor League Stats</h3>
              <div className="roster-style-table-wrap">
                <div>
                  <table className="roster-style-table">
                    <thead>
                      <tr>
                        <th>Season</th>
                        <th style={{ textAlign: 'center' }}>Level</th>
                        <th>Team</th>
                        {isPitcher ? (
                          <>
                            <th style={{ textAlign: 'center' }}>W</th>
                            <th style={{ textAlign: 'center' }}>L</th>
                            <th style={{ textAlign: 'center' }}>SV</th>
                            <th style={{ textAlign: 'center' }}>ERA</th>
                            <th style={{ textAlign: 'center' }}>WHIP</th>
                            <th style={{ textAlign: 'center' }}>K</th>
                            <th style={{ textAlign: 'center' }}>IP</th>
                          </>
                        ) : (
                          <>
                            <th style={{ textAlign: 'center' }}>G</th>
                            <th style={{ textAlign: 'center' }}>AB</th>
                            <th style={{ textAlign: 'center' }}>H</th>
                            <th style={{ textAlign: 'center' }}>AVG</th>
                            <th style={{ textAlign: 'center' }}>HR</th>
                            <th style={{ textAlign: 'center' }}>RBI</th>
                            <th style={{ textAlign: 'center' }}>R</th>
                            <th style={{ textAlign: 'center' }}>SB</th>
                            <th style={{ textAlign: 'center' }}>OPS</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {minorLeagueRows.map((stat, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: '8px', fontWeight: '600' }}>{stat.season}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{stat.level}</td>
                          <td style={{ padding: '8px' }}>{stat.team}</td>
                          {isPitcher ? (
                            <>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.W}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.L}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.SV}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.ERA}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.WHIP}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.K}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.IP}</td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.G}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.AB}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.H}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.AVG}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.HR}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.RBI}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.R}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.SB}</td>
                              <td style={{ padding: '8px', textAlign: 'center' }}>{stat.OPS}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Splits Tab */}
          {activeTab === 'splits' && !loading && (
            <div>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
                  Stat Splits
                </h3>
                <select
                  value={splitsPeriod}
                  onChange={(e) => setSplitsPeriod(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid #dee2e6',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  <option value="current">Current Season</option>
                  <option value="previous">Previous Season</option>
                  <option value="career">Career</option>
                </select>
              </div>

              {splitsData && splitsData[splitsPeriod] ? (
                <div className="roster-style-table-wrap">
                  <div>
                  <table className="roster-style-table">
                    <thead>
                      <tr>
                        {isPitcher ? (
                          <>
                            <th>Stat</th>
                            <th style={{ textAlign: 'center' }}>Value</th>
                          </>
                        ) : (
                          <>
                            <th>Stat</th>
                            <th style={{ textAlign: 'center' }}>Value</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {isPitcher ? (
                        <>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Wins</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].Wins || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Losses</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].Losses || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Saves</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].Saves || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>ERA</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {splitsData[splitsPeriod].EarnedRunAverage 
                                ? formatStat(splitsData[splitsPeriod].EarnedRunAverage, 2)
                                : calculateERA(splitsData[splitsPeriod].EarnedRunsAllowed, splitsData[splitsPeriod].InningsPitchedDecimal)}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>WHIP</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {splitsData[splitsPeriod].WalksHitsPerInningsPitched
                                ? formatStat(splitsData[splitsPeriod].WalksHitsPerInningsPitched, 2)
                                : calculateWHIP(splitsData[splitsPeriod].HitsAllowed, splitsData[splitsPeriod].WalksAllowed, splitsData[splitsPeriod].InningsPitchedDecimal)}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Strikeouts</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].PitcherStrikeouts || splitsData[splitsPeriod].Strikeouts || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Innings Pitched</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{formatStat(splitsData[splitsPeriod].InningsPitchedDecimal, 1)}</td>
                          </tr>
                        </>
                      ) : (
                        <>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Games</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].Games || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>At Bats</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].AtBats || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Hits</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].Hits || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Batting Average</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {splitsData[splitsPeriod].BattingAverage
                                ? formatStat(splitsData[splitsPeriod].BattingAverage, 3)
                                : calculateAVG(splitsData[splitsPeriod].Hits, splitsData[splitsPeriod].AtBats)}
                            </td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Home Runs</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].HomeRuns || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>RBI</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].RunsBattedIn || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Runs</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].Runs || 0}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '8px', fontWeight: '600' }}>Stolen Bases</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{splitsData[splitsPeriod].StolenBases || 0}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              ) : (
                <div style={{ color: '#666', fontStyle: 'italic' }}>No splits data available</div>
              )}
            </div>
          )}

          {/* News Tab */}
          {activeTab === 'news' && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>
                News
              </h3>
              {loading && newsPage === 1 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
              ) : newsItems.length > 0 ? (
                <>
                  {newsItems.map((item, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        borderLeft: '3px solid #007bff'
                      }}
                    >
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                        {item.Updated && new Date(item.Updated).toLocaleDateString()}
                      </div>
                      <div style={{ fontWeight: '600', marginBottom: '5px', fontSize: '16px' }}>
                        {item.Title}
                      </div>
                      <div style={{ fontSize: '14px', color: '#333', marginBottom: '10px' }}>
                        {item.Content}
                      </div>
                      {item.Url && (
                        <a
                          href={item.Url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: '#007bff',
                            fontSize: '14px',
                            textDecoration: 'none'
                          }}
                        >
                          Read more →
                        </a>
                      )}
                    </div>
                  ))}
                  {newsItems.length >= newsPage * 10 && (
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <button
                        onClick={() => setNewsPage(prev => prev + 1)}
                        disabled={loadingMoreNews}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: loadingMoreNews ? 'not-allowed' : 'pointer',
                          fontSize: '14px',
                          opacity: loadingMoreNews ? 0.6 : 1
                        }}
                      >
                        {loadingMoreNews ? 'Loading...' : 'Load 10 More'}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#666', fontStyle: 'italic' }}>No news available</div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && !loading && (
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px' }}>
                Transactions
              </h3>
              {transactions.length > 0 ? (
                <div>
                  {transactions.map((transaction, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        borderLeft: '3px solid #28a745'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                        <div style={{ fontWeight: '600', fontSize: '16px' }}>
                          {transaction.type}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {transaction.date && new Date(transaction.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333', marginBottom: '5px' }}>
                        {transaction.description}
                      </div>
                      {transaction.team && (
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Team: {transaction.team}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#666', fontStyle: 'italic' }}>
                  {leagueId ? 'No transactions found for this player' : 'League ID required to view transactions'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PlayerProfile;
